import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { Notification, getUnreadNotificationsQuery, markAsRead, markAllAsRead, ORG_BROADCAST_USER_ID } from '../lib/notifications';
import { onSnapshot } from 'firebase/firestore';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const NotificationsToggle: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { currentUser } = useAuth();
  const { currentOrganization } = useOrganization();

  useEffect(() => {
    if (!currentUser?.uid || !currentOrganization?.id) return;

    const query = getUnreadNotificationsQuery(currentOrganization.id, currentUser.uid);
    const unsubscribe = onSnapshot(query, (snapshot) => {
      const notificationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Notification[];
      setNotifications(notificationsData);
    });

    return () => unsubscribe();
  }, [currentUser?.uid, currentOrganization?.id]);

  const handleMarkAsRead = async (notificationId: string) => {
    if (!currentOrganization?.id) return;
    await markAsRead(currentOrganization.id, notificationId);
  };

  const handleMarkAllAsRead = async () => {
    if (!currentOrganization?.id || !currentUser?.uid) return;
    await markAllAsRead(currentOrganization.id, currentUser.uid);
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success': return '✅';
      case 'warning': return '⚠️';
      case 'error': return '❌';
      default: return 'ℹ️';
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
      >
        <Bell className="h-5 w-5" />
        {notifications.filter(n => n.userId !== ORG_BROADCAST_USER_ID).length > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {notifications.filter(n => n.userId !== ORG_BROADCAST_USER_ID).length}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="
            sm:absolute sm:right-0 sm:mt-2 sm:w-80
            fixed left-1/2 -translate-x-1/2 top-16 w-[92vw]
            bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-[100]
          "
        >
          <div className="p-4 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Notifications
              </h3>
              {notifications.filter(n => n.userId !== ORG_BROADCAST_USER_ID).length > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Tout marquer comme lu
                </button>
              )}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                Aucune notification
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-xl">
                        {getNotificationIcon(notification.type)}
                      </span>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {notification.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {notification.createdAt?.toDate
                              ? format(new Date(notification.createdAt.toDate()), 'PPp', { locale: fr })
                              : 'Date inconnue'}
                          </span>
                          {notification.userId === ORG_BROADCAST_USER_ID ? (
                            <span className="text-xs text-gray-400" title="Notification organisation - visible par tous">
                              Organisation
                            </span>
                          ) : (
                            <button
                              onClick={() => handleMarkAsRead(notification.id)}
                              className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              Marquer comme lu
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsToggle;